import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useAuthStore } from '../store/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOwner?: boolean;
  requireAdmin?: boolean;
  /**
   * Let an unauthenticated caller through as a guest instead of redirecting
   * home. Used for the checkout pages, which support guest checkout — the
   * backend resolves an unauthenticated caller to a `guest:<uuid>` cart id
   * (see cartIdentity.ts) rather than rejecting the request.
   */
  allowGuest?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireOwner = false,
  requireAdmin = false,
  allowGuest = false,
}) => {
  const auth = useAuth();
  const { user } = useAuthStore();

  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!auth.isAuthenticated && !allowGuest) {
    // Redirect to home, which will trigger Cognito login
    return <Navigate to="/" replace />;
  }

  if (requireOwner && !user?.isOwner) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Owner Access Required</h2>
        <p className="text-gray-600 mb-6">
          You need to be an owner to access this page. Create your first listing to become an
          owner.
        </p>
      </div>
    );
  }

  if (requireAdmin && !user?.isAdmin) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Access Required</h2>
        <p className="text-gray-600">You don't have permission to access this page.</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;

